import FileDownload from "js-file-download";
import { call, put, takeLatest } from "redux-saga/effects";
import { dashboardLoaderAC } from "../system/systemActionCreators";
import { errorGenerator, notificationCreator } from "model/system/utils";
import {
  paginationMapper,
  typePagination,
  typeRequestPagination,
} from "../system/constants";
import {
  exportSalesListWithFilters,
  slimSalesSearch,
} from "model/dashboardSales/dSalesAPI";
import {
  typeSalesFilters,
  typeSlimSalesItem,
} from "model/dashboardSales/dSalesReducer";
import {
  dSalesSetDataAC,
  dSalesSetDataSagaAC,
  dSalesSetPaginationAC,
  dSalesExportDataSagaAC,
  dSalesSelectedExportDataSagaAC,
} from "model/dashboardSales/dSalesActionCreators";

// Temp data list mapper
const tempDataMapper = (tempResponseData: any): typeSlimSalesItem[] => {
  return tempResponseData.map((item: typeSlimSalesItem) => {
    return {
      id: item.id,
      date: item.date,
      amount: item.amount,
      discountAmount: item.discountAmount,
      payments: item.payments,
      store: item.store,
      refunded: item.refunded,
      saleNumber: item.saleNumber,
      salePublicId: item.salePublicId,
      servicePaymentAmount: item.servicePaymentAmount,
      userId: item.userId,
      userName: item.userName,
      appletVersion: item.appletVersion,
      fiscalSign: item.fiscalSign,
      receiptNumber: item.receiptNumber,
      paid: item.paid,
      status: item.status,
      checked: false,
    };
  });
};

// Get data function-generator
function* getData(payload: {
  filters: typeSalesFilters;
  paginationRequest: typeRequestPagination;
}) {
  let response = yield call(slimSalesSearch, payload);

  if (response.status === 200) {
    const tempData: typeSlimSalesItem[] = tempDataMapper(response.data.content);
    const pagination: typePagination = paginationMapper(response.data);

    yield put(dSalesSetPaginationAC(pagination));

    yield put(dSalesSetDataAC(tempData));
  } else {
    yield errorGenerator(response);
  }
}

// Set sales list saga
export function* dSalesSetDataWorker(actionObject: {
  type: string;
  payload: {
    filters: typeSalesFilters;
    paginationRequest: typeRequestPagination;
  };
}) {
  yield put(dashboardLoaderAC(true));

  yield getData(actionObject.payload);

  yield put(dashboardLoaderAC(false));
}

export function* dSalesSetDataWatcher() {
  yield takeLatest(dSalesSetDataSagaAC.type, dSalesSetDataWorker);
}

// Export data saga
export function* dSalesExportDataWorker(actionObject: {
  type: string;
  payload: { filters: typeSalesFilters; lang: string };
}) {
  yield put(dashboardLoaderAC(true));

  let response = yield call(exportSalesListWithFilters, {
    lang: actionObject.payload.lang,
    filters: actionObject.payload.filters,
  });

  if (response.status === 200) {
    if (response?.data?.exportType === "SYNC" && response?.data?.result) {
      const binaryString = atob(response?.data?.result);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);

      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      FileDownload(blob, "sales.xlsx");
    } else {
      yield notificationCreator("SUCCESS_EXPORT");
    }
  } else {
    yield errorGenerator(response);
  }

  yield put(dashboardLoaderAC(false));
}

export function* dSalesExportDataWatcher() {
  yield takeLatest(dSalesExportDataSagaAC.type, dSalesExportDataWorker);
}

// Export selected sales data saga
export function* dSalesSelectedExportDataWorker(actionObject: {
  type: string;
  payload: { salesIds: string[]; lang: string };
}) {
  yield put(dashboardLoaderAC(true));

  let response = yield call(exportSalesListWithFilters, {
    selectedSaleIds: actionObject.payload.salesIds,
    lang: actionObject.payload.lang,
  });

  if (response.status === 200) {
    if (response?.data?.exportType === "SYNC" && response?.data?.result) {
      const binaryString = atob(response?.data?.result);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);

      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      FileDownload(blob, "selected-sales.xlsx");
    } else {
      yield notificationCreator("SUCCESS_EXPORT");
    }
  } else {
    yield errorGenerator(response);
  }

  yield put(dashboardLoaderAC(false));
}

export function* dSalesSelectedExportDataWatcher() {
  yield takeLatest(
    dSalesSelectedExportDataSagaAC.type,
    dSalesSelectedExportDataWorker,
  );
}
